import React, { useEffect } from 'react';
import { useDebounce } from 'usehooks-ts';
import type { CustomerStateType } from 'src/routes/Checkout/store/domains/customer/types';
import { useFormikContext } from 'formik';
import styled, { css } from 'styled-components';
import { useTranslation } from '@lt/localization';
import FormHeader from './components/FormHeader';
import CustomerCitizenship from './components/CustomerCitizenship';
import type { Citizenship } from '../../types';
import CustomerInput, { CustomInput } from './components/CustomerInput';
import SubmitCustomerMobile from './components/SubmitCustomerMobile';
import CustomerPatronymicInput from './components/CustomerPatronymicInput';
import CustomerPassportNumberInput from './components/CustomerPassportNumberInput';
import { RUSSIA_NATIONALITY_ID } from './constants';
import CustomerBirthday, {
  Input as BirthdayCustomInput,
} from './components/CustomerBirthday';

type CustomerProps = {
  saveCustomerInfo: (customerInfo: CustomerStateType) => void;
  touristData: CustomerStateType;
  citizenships: Citizenship[];
  isMobile: boolean;
  showFillerButton: boolean;
  onSubmit: () => void;
  mobileSubmitBtnDisabled: boolean;
};

export const CustomerForm = (props: CustomerProps): JSX.Element => {
  const {
    citizenships,
    touristData,
    isMobile,
    saveCustomerInfo,
    showFillerButton,
    onSubmit,
    mobileSubmitBtnDisabled,
  } = props;

  const { t } = useTranslation();

  const { setValues, values, setTouched } =
    useFormikContext<CustomerStateType>();

  const setCustomerDataFromTourists = async () => {
    // Трогаем все поля, чтобы они засветились зеленым или красным (визуальная валидация)
    setTouched(
      Object.keys(values).reduce((touched, field) => {
        touched[field] = true;
        return touched;
      }, {}),
    );
    setValues(touristData, true);
  };

  const debouncedValues = useDebounce(values, 100);
  useEffect(() => {
    saveCustomerInfo(debouncedValues);
  }, [debouncedValues, saveCustomerInfo]);

  const isRussian = values.nationalityId === RUSSIA_NATIONALITY_ID;

  return (
    <FormContainer>
      <FormHeader
        isMobile={isMobile}
        showFillerButton={showFillerButton}
        setCustomerDataFromTourists={setCustomerDataFromTourists}
      />
      <FormContent $isMobile={isMobile}>
        <Row $isMobile={isMobile}>
          <SurnameInput
            name="surname"
            header={t('checkout.customer.form_fields.surname')}
            placeholder={
              isRussian
                ? t('checkout.customer.form_fields.surname')
                : t('checkout.customer.form_fields.surname_en')
            }
            isMobile={isMobile}
          />
          <NameInput
            name="name"
            header={t('checkout.customer.form_fields.name')}
            placeholder={
              isRussian
                ? t('checkout.customer.form_fields.name')
                : t('checkout.customer.form_fields.name_en')
            }
            isMobile={isMobile}
          />
          {isRussian && (
            <PatronymicInput
              name="patronymic"
              header={t('checkout.customer.form_fields.patronymic')}
              placeholder={t('checkout.customer.form_fields.patronymic')}
              isMobile={isMobile}
            />
          )}

          <BirthdayInput
            name="birthday"
            header={t('checkout.customer.form_fields.birthday')}
            placeholder={t(
              'checkout.customer.form_fields.birthday_placeholder',
            )}
            isMobile={isMobile}
          />

          <InnerRow $isMobile={isMobile}>
            <StyledCustomerCitizenship
              citizenships={citizenships}
              isMobile={isMobile}
            />
            <PassportNumberInput
              name="passportNumber"
              header={t('checkout.customer.form_fields.passport_number')}
              placeholder={
                values.docType === 'internal_passport'
                  ? '0000 000000'
                  : '00 000000'
              }
              isMobile={isMobile}
            />
          </InnerRow>
        </Row>

        {/* <Row>
            <AddressInput
              name="address"
              header="Адрес прописки"
              placeholder="Адрес прописки"
              showSuccessIcon={false}
            />
          </Row> */}
        {isMobile && (
          <SubmitCustomerMobile
            onSubmit={onSubmit}
            disabled={mobileSubmitBtnDisabled}
          />
        )}
      </FormContent>
    </FormContainer>
  );
};

const FormContent = styled.div<{ $isMobile: boolean }>`
  padding: 8px;
  border-radius: 6px;
  background: #f3f2e8;

  ${(props) =>
    props.$isMobile &&
    css`
      position: relative;
      padding-bottom: 18px;

      &::after {
        position: absolute;
        right: calc(28% - 10px);
        bottom: 0;
        margin-bottom: 0;
        width: 44%;
        height: 0;
        content: '';
        border-top: 10px solid #fff;
        border-left: 10px solid rgba(0, 0, 0, 0);
        border-right: 10px solid rgba(0, 0, 0, 0);
        border-radius: 0 1px 4px 0;
        transform: rotateZ(180deg);
      }
    `}
`;

const FormContainer = styled.div`
  padding: 8px;
  border-radius: 8px;
  background: white;
`;

const Row = styled.div<{ $isMobile: boolean }>`
  display: flex;
  width: 100%;

  ${(props) =>
    props.$isMobile &&
    css`
      flex-direction: column;
    `}
`;

const NameInput = styled(CustomerInput)<{ isMobile: boolean }>`
  flex: 1;

  ${CustomInput} {
    border-right: 0;
  }

  ${(props) =>
    props.isMobile &&
    css`
      ${CustomInput} {
        border-radius: 4px;
        border-right: 1px solid #c2c5cc;
      }

      margin-bottom: 8px;
    `}
`;

const SurnameInput = styled(CustomerInput)<{ isMobile: boolean }>`
  flex: 1;

  ${CustomInput} {
    border-radius: 4px 0 0 4px;
    border-right: 0;
  }

  ${(props) =>
    props.isMobile &&
    css`
      ${CustomInput} {
        border-radius: 4px;
        border-right: 1px solid #c2c5cc;
      }

      margin-bottom: 8px;
    `}
`;

const PatronymicInput = styled(CustomerPatronymicInput)<{ isMobile: boolean }>`
  flex: 1;

  ${CustomInput} {
    border-right: 0;
  }

  ${(props) =>
    props.isMobile &&
    css`
      ${CustomInput} {
        border-radius: 4px;
        border-right: 1px solid #c2c5cc;
      }

      margin-bottom: 8px;
    `}
`;

const BirthdayInput = styled(CustomerBirthday)<{ isMobile: boolean }>`
  flex: 1;

  ${BirthdayCustomInput} {
    border-right: 0;
  }

  ${(props) =>
    props.isMobile &&
    css`
      ${BirthdayCustomInput} {
        border-radius: 4px;
        border-right: 1px solid #c2c5cc;
      }

      margin-bottom: 8px;
    `}
`;

const StyledCustomerCitizenship = styled(CustomerCitizenship)<{
  isMobile: boolean;
}>`
  ${(props) =>
    props.isMobile &&
    css`
      flex: 1;
    `}
`;

const InnerRow = styled.div<{ $isMobile: boolean }>`
  display: flex;
  ${(props) =>
    props.$isMobile &&
    css`
      width: 100%;
    `}
`;

const PassportNumberInput = styled(CustomerPassportNumberInput)<{
  isMobile: boolean;
}>`
  ${CustomInput} {
    border-radius: 0 4px 4px 0;
    border-left: 0;
    border-right: 1px solid #c2c5cc;
  }

  ${(props) =>
    props.isMobile &&
    css`
      flex: 1;
    `}
`;

// const AddressInput = styled(Form.Input)`
//   width: 100%;
// `;
